/**
 * Stripe Service
 *
 * Handles all Stripe-related operations on the client side:
 * - Creating checkout sessions (subscriptions + one-time purchases)
 * - Managing shopping cart
 * - Redirecting to Stripe Customer Portal
 * - Fetching order history
 *
 * Server-side Stripe operations are in Netlify Functions.
 */

import { loadStripe } from '@stripe/stripe-js';
import logger from '../utils/logger.js';

class StripeService {
  constructor() {
    // Use local functions for development, production API for live
    const hostname =
      (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
    const port = (typeof window !== 'undefined' && window.location && window.location.port) || '';

    const isDevelopment =
      process.env.NODE_ENV === 'development' ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      port === '8888';

    this.baseUrl = isDevelopment ? '/.netlify/functions' : 'https://www.clothedeye.com/api';

    // Stripe publishable key (test mode for development, live for production)
    this.stripePublishableKey = isDevelopment
      ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST
      : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_LIVE;

    this.stripePromise = null;

    logger.log(`💳 StripeService initialized (${isDevelopment ? 'TEST mode' : 'LIVE mode'})`);
  }

  /**
   * Get Stripe instance (lazy load)
   */
  async getStripe() {
    if (!this.stripePromise) {
      if (!this.stripePublishableKey) {
        throw new Error('Stripe publishable key not configured');
      }
      this.stripePromise = loadStripe(this.stripePublishableKey);
    }
    return this.stripePromise;
  }

  // ==========================================================================
  // SUBSCRIPTION CHECKOUT
  // ==========================================================================

  /**
   * Create subscription checkout session
   *
   * @param {string} priceId - Stripe price ID (monthly or annual)
   * @param {string} authToken - User's JWT token
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async createSubscriptionCheckout(priceId, authToken) {
    try {
      logger.log('💳 Creating subscription checkout session...');

      const response = await fetch(`${this.baseUrl}/stripe-create-subscription-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ priceId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      const stripe = await this.getStripe();
      const { error } = await stripe.redirectToCheckout({
        sessionId: data.sessionId,
      });

      if (error) {
        throw new Error(error.message);
      }

      return { success: true };
    } catch (error) {
      logger.error('❌ Subscription checkout error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // ==========================================================================
  // ONE-TIME PURCHASE CHECKOUT
  // ==========================================================================

  /**
   * Create one-time purchase checkout session
   *
   * @param {Array} items - Cart items [{listingId, size, color, quantity, price}]
   * @param {string} authToken - User's JWT token (optional for guest checkout)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async createPurchaseCheckout(items, authToken = null) {
    try {
      logger.log('💳 Creating purchase checkout session...', items);

      const headers = {
        'Content-Type': 'application/json',
      };

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${this.baseUrl}/stripe-create-purchase-checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      const stripe = await this.getStripe();
      const { error } = await stripe.redirectToCheckout({
        sessionId: data.sessionId,
      });

      if (error) {
        throw new Error(error.message);
      }

      return { success: true };
    } catch (error) {
      logger.error('❌ Purchase checkout error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // ==========================================================================
  // CUSTOMER PORTAL
  // ==========================================================================

  /**
   * Redirect to Stripe Customer Portal (manage subscription, payment methods, invoices)
   *
   * @param {string} authToken - User's JWT token
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  async openCustomerPortal(authToken) {
    try {
      logger.log('💳 Opening Stripe Customer Portal...');

      const response = await fetch(`${this.baseUrl}/stripe-customer-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to open customer portal');
      }

      // Redirect to Stripe Customer Portal
      window.location.href = data.url;

      return { success: true };
    } catch (error) {
      logger.error('❌ Customer portal error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // ==========================================================================
  // ORDER HISTORY
  // ==========================================================================

  /**
   * Get user's order history
   *
   * @param {string} authToken - User's JWT token
   * @returns {Promise<{success: boolean, orders?: Array, error?: string}>}
   */
  async getOrderHistory(authToken) {
    try {
      logger.log('📦 Fetching order history...');

      const response = await fetch(`${this.baseUrl}/stripe-get-orders`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch orders');
      }

      return {
        success: true,
        orders: data.orders,
      };
    } catch (error) {
      logger.error('❌ Order history error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get single order details
   *
   * @param {string} orderId - Order ID
   * @param {string} authToken - User's JWT token
   * @returns {Promise<{success: boolean, order?: Object, error?: string}>}
   */
  async getOrder(orderId, authToken) {
    try {
      logger.log(`📦 Fetching order ${orderId}...`);

      const response = await fetch(`${this.baseUrl}/stripe-get-order/${orderId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch order');
      }

      return {
        success: true,
        order: data.order,
      };
    } catch (error) {
      logger.error('❌ Order fetch error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // ==========================================================================
  // SUBSCRIPTION PLANS
  // ==========================================================================

  /**
   * Get available subscription plans
   *
   * @returns {Promise<{success: boolean, plans?: Array, error?: string}>}
   */
  async getSubscriptionPlans() {
    try {
      logger.log('📋 Fetching subscription plans...');

      const response = await fetch(`${this.baseUrl}/stripe-get-plans`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch plans');
      }

      return {
        success: true,
        plans: data.plans,
      };
    } catch (error) {
      logger.error('❌ Plans fetch error:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // ==========================================================================
  // PRICE FORMATTING
  // ==========================================================================

  /**
   * Format price in cents to display string
   *
   * @param {number} cents - Price in cents
   * @param {string} currency - Currency code (default: 'usd')
   * @returns {string} Formatted price (e.g., "$19.99")
   */
  formatPrice(cents, currency = 'usd') {
    const dollars = cents / 100;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(dollars);
  }
}

// Export singleton instance
const stripeService = new StripeService();
export default stripeService;
